import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { formatDate } from '../../../utils/formatDate';
import TextInput from '../../../sharedModules/form/components/TextInput';
import InspectionRadioGroup from '../components/InspectionRadioGroup';
import { usePost } from '../../../sharedModules/hooks/useQuery';
import axios from '../../../sharedModules/utils/axios.utils';
import { conditionOptions, conditionWithNaOptions, yesNoOptions } from '../config/options';
import SystemAccordionList from '../components/SystemAccordionList';
import HvacSystemFields, { defaultHvacSystem } from '../components/HvacSystemFields';
import type { HvacSystemValues } from '../components/HvacSystemFields';
import PlumbingSystemFields, { defaultPlumbingSystem } from '../components/PlumbingSystemFields';
import type { PlumbingSystemValues } from '../components/PlumbingSystemFields';
import MediaUploadSection from '../../../sharedModules/mediaUpload/MediaUploadSection';
import PreviousInspectionsTable from '../components/PreviousInspectionsTable';
import PreviousInspectionModal from '../components/PreviousInspectionModal';
import { mapApiResponseToFormValues } from '../utils/mapApiResponseToFormValues';
import type { ApiInspectionReport } from '../utils/mapApiResponseToFormValues';

/** Convert camelCase key to snake_case */
function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function systemToSnake(sys: object): Record<string, unknown> {
  return Object.fromEntries(Object.entries(sys).map(([key, value]) => [toSnakeCase(key), value]));
}

export const initialFormValues = {
  customerName: '',
  technician: '',
  date: '',
  workOrderNumber: '',
  offeredMaintenancePlan: '',

  // Electrical
  electricalManufacturer: '',
  electricalOverallVisualCondition: '',
  electricalWholeHouseSurgeProtectionNA: false,
  electricalWholeHouseSurgeProtection: '',
  electricalWiringSecuredProperlyTerminated: '',
  electricalWholeHomeGeneratorNA: false,
  electricalWholeHomeGeneratorCondition: '',
  electricalProperClearance: '',
  electricalNotes: '',

  // Water Treatment
  waterTreatmentNA: false,
  waterTreatmentManufacturer: '',
  waterTreatmentTypeWaterSoftener: false,
  waterTreatmentTypeReverseOsmosis: false,
  waterTreatmentTypeWholeHouseFilter: false,
  waterTreatmentVisibleCondition: '',
  waterTreatmentRecommendedAnnualMaintenance: '',
  waterTreatmentFiltersOrSaltRequired: '',
  waterTreatmentWaterQualityComplaints: '',
  waterTreatmentLocationForSolution: '',
  waterTreatmentNotes: '',

  // Indoor Air Quality
  airQualityAirFilterCondition: '',
  airQualityHumidifierStatus: '',
  airQualityDehumidifierStatus: '',
  airQualityNotes: '',
};

export type InspectionFormValues = typeof initialFormValues;

interface InspectionReportFormPageProps {
  homeInspectionJobId?: string;
  prefillData?: Partial<InspectionFormValues>;
  headerDisabled?: boolean;
  title?: string;
  onSubmitSuccess?: () => void;
}

const sectionHeaderSx = {
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
  bgcolor: '#3d5a80',
  px: 2,
  py: 1,
  borderRadius: '4px 4px 0 0',
};

const subHeaderSx = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#3d5a80',
  bgcolor: '#e8eef4',
  px: 2,
  py: 0.75,
  borderRadius: 1,
  mb: 1.5,
};

interface ManufacturerOption {
  value: string;
  label: string;
}

const InspectionReportFormPage: React.FC<InspectionReportFormPageProps> = ({
  homeInspectionJobId,
  prefillData,
  headerDisabled = false,
  title = 'Home Services Inspection Report',
  onSubmitSuccess,
}) => {
  const [formValues, setFormValues] = useState(initialFormValues);
  const [hvacSystems, setHvacSystems] = useState<HvacSystemValues[]>([{ ...defaultHvacSystem }]);
  const [plumbingSystems, setPlumbingSystems] = useState<PlumbingSystemValues[]>([{ ...defaultPlumbingSystem }]);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hvacSystemPhotos, setHvacSystemPhotos] = useState<File[][]>([[]]);
  const [plumbingSystemPhotos, setPlumbingSystemPhotos] = useState<File[][]>([[]]);
  const [electricalPhotos, setElectricalPhotos] = useState<File[]>([]);
  const [waterTreatmentPhotos, setWaterTreatmentPhotos] = useState<File[]>([]);
  const [airQualityPhotos, setAirQualityPhotos] = useState<File[]>([]);
  const [hvacManufacturers, setHvacManufacturers] = useState<ManufacturerOption[]>([]);
  const [plumbingManufacturers, setPlumbingManufacturers] = useState<ManufacturerOption[]>([]);
  const [electricalManufacturers, setElectricalManufacturers] = useState<ManufacturerOption[]>([]);
  const [waterTreatmentManufacturers, setWaterTreatmentManufacturers] = useState<ManufacturerOption[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInspectionId, setSelectedInspectionId] = useState<number | null>(null);

  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        const [hvacRes, plumbingRes, electricalRes, waterRes]: any[] = await Promise.all([
          axios.get('/home_services/manufacturers', { params: { u_trade: 'HVAC' } }),
          axios.get('/home_services/manufacturers', { params: { u_trade: 'Plumbing' } }),
          axios.get('/home_services/manufacturers', { params: { u_trade: 'Electrical' } }),
          axios.get('/home_services/manufacturers', { params: { u_trade: 'Water Treatment' } }),
        ]);
        setHvacManufacturers(hvacRes.data.result || []);
        setPlumbingManufacturers(plumbingRes.data.result || []);
        setElectricalManufacturers(electricalRes.data.result || []);
        setWaterTreatmentManufacturers(waterRes.data.result || []);
      } catch {
        // Silently fail - dropdowns will just be empty
      }
    };
    fetchManufacturers();
  }, []);

  useEffect(() => {
    if (prefillData) {
      setFormValues((prev) => ({ ...prev, ...prefillData }));
    }
  }, [prefillData]);

  const updateField = (field: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    setHvacSystemPhotos((prev) => {
      if (prev.length === hvacSystems.length) return prev;
      if (hvacSystems.length > prev.length) {
        return [...prev, ...Array.from({ length: hvacSystems.length - prev.length }, () => [] as File[])];
      }
      return prev.slice(0, hvacSystems.length);
    });
  }, [hvacSystems.length]);

  useEffect(() => {
    setPlumbingSystemPhotos((prev) => {
      if (prev.length === plumbingSystems.length) return prev;
      if (plumbingSystems.length > prev.length) {
        return [...prev, ...Array.from({ length: plumbingSystems.length - prev.length }, () => [] as File[])];
      }
      return prev.slice(0, plumbingSystems.length);
    });
  }, [plumbingSystems.length]);

  const handleOpenPreviousInspection = (inspectionId: number) => {
    setSelectedInspectionId(inspectionId);
    setModalOpen(true);
  };

  const handlePrefill = (data: ApiInspectionReport) => {
    const {
      formValues: mapped,
      hvacSystems: mappedHvac,
      plumbingSystems: mappedPlumbing,
    } = mapApiResponseToFormValues(data);
    setFormValues((prev) => ({ ...prev, ...mapped }));
    setHvacSystems(mappedHvac.length > 0 ? mappedHvac : [{ ...defaultHvacSystem }]);
    setPlumbingSystems(mappedPlumbing.length > 0 ? mappedPlumbing : [{ ...defaultPlumbingSystem }]);
  };

  const updateSystemPhotos = (type: 'hvac' | 'plumbing', index: number, files: File[]) => {
    const setter = type === 'hvac' ? setHvacSystemPhotos : setPlumbingSystemPhotos;
    setter((prev) => {
      const updated = [...prev];
      updated[index] = files;
      return updated;
    });
  };

  const { mutateAsync, isPending, reset, error } = usePost({
    onSuccess: () => {
      if (onSubmitSuccess) {
        onSubmitSuccess();
      } else {
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 3000);
      }
    },
    onError: (err) => {
      setSubmitError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'Something went wrong saving the report.',
      );
    },
  });

  const errorMessage =
    submitError ||
    (error && typeof error === 'object' && 'response' in error
      ? (error as { response?: { data?: { errors?: string[]; error?: string } } })?.response?.data?.errors?.[0] ||
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to update report'
      : null);

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!homeInspectionJobId) return;

    try {
      const formData = new FormData();
      formData.append('inspection_report[home_inspection_job_id]', homeInspectionJobId);
      formData.append('inspection_report[offered_maintenance_plan]', formValues.offeredMaintenancePlan);

      // HVAC systems as nested JSON array
      const hvacPayload = hvacSystems.map((sys) => systemToSnake(sys));
      formData.append('inspection_report[hvac_systems]', JSON.stringify(hvacPayload));

      // Plumbing systems as nested JSON array
      const plumbingPayload = plumbingSystems.map((sys) => systemToSnake(sys));
      formData.append('inspection_report[plumbing_systems]', JSON.stringify(plumbingPayload));

      // Electrical (flat, snake_case)
      Object.entries(formValues).forEach(([key, value]) => {
        if (key.startsWith('electrical') || key.startsWith('waterTreatment') || key.startsWith('airQuality')) {
          formData.append(`inspection_report[${toSnakeCase(key)}]`, String(value));
        }
      });

      hvacSystemPhotos.forEach((files, sysIdx) => {
        files.forEach((file) => {
          formData.append(`inspection_report[hvac_systems_photos][${sysIdx}][]`, file);
        });
      });

      plumbingSystemPhotos.forEach((files, sysIdx) => {
        files.forEach((file) => {
          formData.append(`inspection_report[plumbing_systems_photos][${sysIdx}][]`, file);
        });
      });

      electricalPhotos.forEach((file) => formData.append('inspection_report[electrical_photos][]', file));
      waterTreatmentPhotos.forEach((file) => formData.append('inspection_report[water_treatment_photos][]', file));
      airQualityPhotos.forEach((file) => formData.append('inspection_report[air_quality_photos][]', file));

      await mutateAsync({ url: '/home_services/inspection_reports', data: formData });
    } catch (err: any) {
      const message =
        err?.response?.data?.error || err?.response?.data?.message || 'Something went wrong saving the report.';
      setSubmitError(message);
    }
  };

  return (
    <>
      <Backdrop
        open={isPending}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={48} />
        <Typography variant="h6" color="inherit">
          Submitting report...
        </Typography>
      </Backdrop>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          {title}
        </Typography>

        {submitted && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Report updated successfully!
          </Alert>
        )}

        {errorMessage && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => {
              setSubmitError(null);
              reset?.();
            }}
          >
            {errorMessage}
          </Alert>
        )}

        {/* Customer Information */}
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Typography sx={sectionHeaderSx}>Customer Information</Typography>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Name"
                  value={formValues.customerName}
                  valueChanged={(val) => updateField('customerName', val)}
                  disabled={headerDisabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Date"
                  value={formatDate(formValues.date)}
                  valueChanged={(val) => updateField('date', val)}
                  disabled={headerDisabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Technician"
                  value={formValues.technician}
                  valueChanged={(val) => updateField('technician', val)}
                  disabled={headerDisabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Work Order Number"
                  value={formValues.workOrderNumber}
                  valueChanged={(val) => updateField('workOrderNumber', val)}
                  disabled={headerDisabled}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Offered Maintenance Plan"
                  radioOptions={yesNoOptions}
                  value={formValues.offeredMaintenancePlan}
                  setValue={(val) => updateField('offeredMaintenancePlan', val)}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <PreviousInspectionsTable customerName={formValues.customerName} onView={handleOpenPreviousInspection} />

        {/* HVAC System Section */}
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Typography sx={sectionHeaderSx}>HVAC System</Typography>
          <Box sx={{ p: 2 }}>
            <SystemAccordionList<HvacSystemValues>
              sectionLabel="HVAC"
              systems={hvacSystems}
              onSystemsChange={setHvacSystems}
              defaultSystem={defaultHvacSystem}
              renderFields={(sys, idx, onChange) => (
                <>
                  <HvacSystemFields values={sys} onChange={onChange} manufacturers={hvacManufacturers} />
                  <MediaUploadSection
                    label={`HVAC System ${idx + 1} Photos`}
                    files={hvacSystemPhotos[idx] || []}
                    onChange={(files) => updateSystemPhotos('hvac', idx, files)}
                  />
                </>
              )}
            />
          </Box>
        </Paper>

        {/* Indoor Air Quality Section */}
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Typography sx={sectionHeaderSx}>Indoor Air Quality</Typography>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Air Filter Condition"
                  radioOptions={conditionOptions}
                  value={formValues.airQualityAirFilterCondition}
                  setValue={(val) => updateField('airQualityAirFilterCondition', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Humidifier Status"
                  radioOptions={conditionWithNaOptions}
                  value={formValues.airQualityHumidifierStatus}
                  setValue={(val) => updateField('airQualityHumidifierStatus', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Dehumidifier Status"
                  radioOptions={conditionWithNaOptions}
                  value={formValues.airQualityDehumidifierStatus}
                  setValue={(val) => updateField('airQualityDehumidifierStatus', val)}
                />
              </Grid>
            </Grid>
            <TextInput
              label="Air Quality Notes"
              value={formValues.airQualityNotes}
              valueChanged={(val) => updateField('airQualityNotes', val)}
              multiline
              rows={3}
            />
            <MediaUploadSection label="Air Quality Photos" files={airQualityPhotos} onChange={setAirQualityPhotos} />
          </Box>
        </Paper>

        {/* Plumbing Section */}
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Typography sx={sectionHeaderSx}>Plumbing</Typography>
          <Box sx={{ p: 2 }}>
            <SystemAccordionList<PlumbingSystemValues>
              sectionLabel="Plumbing"
              systems={plumbingSystems}
              onSystemsChange={setPlumbingSystems}
              defaultSystem={defaultPlumbingSystem}
              renderFields={(sys, idx, onChange) => (
                <>
                  <PlumbingSystemFields values={sys} onChange={onChange} manufacturers={plumbingManufacturers} />
                  <MediaUploadSection
                    label={`Plumbing System ${idx + 1} Photos`}
                    files={plumbingSystemPhotos[idx] || []}
                    onChange={(files) => updateSystemPhotos('plumbing', idx, files)}
                  />
                </>
              )}
            />
          </Box>
        </Paper>

        {/* Electrical Section */}
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Typography sx={sectionHeaderSx}>Electrical</Typography>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="filled">
                  <InputLabel>Manufacturer</InputLabel>
                  <Select
                    value={formValues.electricalManufacturer}
                    onChange={(e) => updateField('electricalManufacturer', e.target.value)}
                  >
                    {electricalManufacturers.map((opt) => (
                      <MenuItem key={opt.value} value={opt.label}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography sx={subHeaderSx}>Inspection Details</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Overall/Visual Condition"
                  radioOptions={conditionOptions}
                  value={formValues.electricalOverallVisualCondition}
                  setValue={(val) => updateField('electricalOverallVisualCondition', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Whole House Surge Protection"
                  radioOptions={conditionWithNaOptions}
                  value={formValues.electricalWholeHouseSurgeProtection}
                  setValue={(val) => updateField('electricalWholeHouseSurgeProtection', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Wiring Secured / Properly Terminated"
                  radioOptions={conditionOptions}
                  value={formValues.electricalWiringSecuredProperlyTerminated}
                  setValue={(val) => updateField('electricalWiringSecuredProperlyTerminated', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Whole Home Generator"
                  radioOptions={conditionWithNaOptions}
                  value={formValues.electricalWholeHomeGeneratorCondition}
                  setValue={(val) => updateField('electricalWholeHomeGeneratorCondition', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Proper Clearance Around Equipment (36 in)"
                  radioOptions={yesNoOptions}
                  value={formValues.electricalProperClearance}
                  setValue={(val) => updateField('electricalProperClearance', val)}
                />
              </Grid>
            </Grid>

            <TextInput
              label="Electrical Notes"
              value={formValues.electricalNotes}
              valueChanged={(val) => updateField('electricalNotes', val)}
              multiline
              rows={3}
            />
            <MediaUploadSection label="Electrical Photos" files={electricalPhotos} onChange={setElectricalPhotos} />
          </Box>
        </Paper>

        {/* Water Treatment Section */}
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ ...sectionHeaderSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#fff' }}>Water Treatment</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.waterTreatmentNA}
                  onChange={(e) => updateField('waterTreatmentNA', e.target.checked)}
                  sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                />
              }
              label={<Typography sx={{ color: '#fff', fontSize: '14px' }}>N/A</Typography>}
            />
          </Box>

          {!formValues.waterTreatmentNA && (
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="filled">
                    <InputLabel>Manufacturer</InputLabel>
                    <Select
                      value={formValues.waterTreatmentManufacturer}
                      onChange={(e) => updateField('waterTreatmentManufacturer', e.target.value)}
                    >
                      {waterTreatmentManufacturers.map((opt) => (
                        <MenuItem key={opt.value} value={opt.label}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography sx={subHeaderSx}>Type</Typography>
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.waterTreatmentTypeWaterSoftener}
                      onChange={(e) => updateField('waterTreatmentTypeWaterSoftener', e.target.checked)}
                    />
                  }
                  label="Water Softener"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.waterTreatmentTypeReverseOsmosis}
                      onChange={(e) => updateField('waterTreatmentTypeReverseOsmosis', e.target.checked)}
                    />
                  }
                  label="Reverse Osmosis"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.waterTreatmentTypeWholeHouseFilter}
                      onChange={(e) => updateField('waterTreatmentTypeWholeHouseFilter', e.target.checked)}
                    />
                  }
                  label="Whole House Filter"
                />
              </Box>

              <Typography sx={subHeaderSx}>Inspection Details</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <InspectionRadioGroup
                    label="Visible Condition Of Equipment"
                    radioOptions={conditionOptions}
                    value={formValues.waterTreatmentVisibleCondition}
                    setValue={(val) => updateField('waterTreatmentVisibleCondition', val)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InspectionRadioGroup
                    label="Recommended Annual Maintenance"
                    radioOptions={yesNoOptions}
                    value={formValues.waterTreatmentRecommendedAnnualMaintenance}
                    setValue={(val) => updateField('waterTreatmentRecommendedAnnualMaintenance', val)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InspectionRadioGroup
                    label="Are Filters Or Salt Required"
                    radioOptions={yesNoOptions}
                    value={formValues.waterTreatmentFiltersOrSaltRequired}
                    setValue={(val) => updateField('waterTreatmentFiltersOrSaltRequired', val)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InspectionRadioGroup
                    label="Are There Water Quality Complaints"
                    radioOptions={yesNoOptions}
                    value={formValues.waterTreatmentWaterQualityComplaints}
                    setValue={(val) => updateField('waterTreatmentWaterQualityComplaints', val)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InspectionRadioGroup
                    label="Location For Water Treatment Solution"
                    radioOptions={yesNoOptions}
                    value={formValues.waterTreatmentLocationForSolution}
                    setValue={(val) => updateField('waterTreatmentLocationForSolution', val)}
                  />
                </Grid>
              </Grid>

              <MediaUploadSection
                label="Water Treatment Photos"
                files={waterTreatmentPhotos}
                onChange={setWaterTreatmentPhotos}
              />
            </Box>
          )}

          <Box sx={{ p: 2, pt: !formValues.waterTreatmentNA ? 0 : 2 }}>
            <TextInput
              label="Water Treatment Notes"
              value={formValues.waterTreatmentNotes}
              valueChanged={(val) => updateField('waterTreatmentNotes', val)}
              multiline
              rows={3}
            />
          </Box>
        </Paper>

        {/* Submit */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 4 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={isPending}
            sx={{
              bgcolor: '#3d5a80',
              '&:hover': { bgcolor: '#2c4460' },
              px: 4,
            }}
          >
            {isPending ? 'Saving...' : homeInspectionJobId ? 'Submit Report' : 'Update Report'}
          </Button>
        </Box>
      </Box>

      <PreviousInspectionModal
        key={selectedInspectionId}
        inspectionId={selectedInspectionId}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onPrefill={handlePrefill}
      />
    </>
  );
};

export default InspectionReportFormPage;
